import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TagIcon from "@mui/icons-material/Tag";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Fab,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Scrollbar from "src/components/scrollbar/Scrollbar";
import { formatDateRange } from "src/utils/date-range";
import ReportsDialog from "../dialogs/reports-dialog";
import useReportsSection from "./use-reports-section";

export default function ReportsSection() {
  const {
    activityStatus,
    addDialog,
    data,
    handleSelectActivity,
    isDropdownOpen,
    isLoading,
    isSelected,
    isTheirNoData,
    pagination,
    selected,

    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    handleCancelActivity,
    isActivityCancelable,
    isCancellingActivity,
  } = useReportsSection();

  return (
    <Stack sx={{ m: 1 }} spacing={1}>
      <Box
        sx={{
          backgroundColor: "#2b2b2b",
          borderRadius: 2,
          p: 1,
          display: "flex",
          alignItems: "center",
          color: "white",
        }}
      >
        <TagIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle1" fontWeight={600}>
          Keyword
        </Typography>
        <Tooltip title={selected.keyword} placement="top" arrow>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{
              ml: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selected.keyword}
          </Typography>
        </Tooltip>

        <IconButton
          onClick={isDropdownOpen.onToggle}
          size="small"
          sx={{ color: "white", ml: "auto" }}
        >
          <ArrowDropDownIcon
            sx={{
              transform: isDropdownOpen.value ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </IconButton>
      </Box>

      <Divider variant="fullWidth" sx={{ borderStyle: "dashed" }} />

      {/* Dropdown Section */}
      {isDropdownOpen.value && (
        <Box
          sx={{
            p: 1,
            bgcolor: "white",
            borderRadius: 3,
            boxShadow: 1,
            position: "relative",
          }}
        >
          <List disablePadding>
            <Scrollbar
              sx={{
                height: 125,
                "& .simplebar-content":
                  isLoading || isTheirNoData
                    ? {
                        alignItems: "center",
                        justifyContent: "center",
                      }
                    : {},
              }}
            >
              {isLoading ? (
                <CircularProgress />
              ) : isTheirNoData ? (
                <Typography color="text.disabled">No keywords found</Typography>
              ) : (
                data?.pages?.map((page) =>
                  page?.activities.map((activity) => {
                    const { color, icon } = activityStatus(activity);
                    return (
                      <ListItemButton
                        key={activity.id}
                        onClick={() => handleSelectActivity(activity)}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          bgcolor: isSelected(activity) ? "#e6ffff" : "transparent",
                          opacity: activity.status === "cancelled" ? 0.6 : 1,
                        }}
                        disabled={activity.status === "cancelled"}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: "#aefcf3",
                              color: "#2abda4",
                              width: 30,
                              height: 30,
                            }}
                          >
                            <TagIcon fontSize="small" sx={{ width: "80%", height: "80%" }} />
                          </Avatar>
                        </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography fontWeight={600} textTransform="capitalize">
                              {icon}
                              {activity.keyword}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.disabled">
                                {activity.platform} /{" "}
                                {formatDateRange(activity.start_date, activity.end_date)}
                              </Typography>
                            }
                          />
                        {isActivityCancelable(activity) && (
                          <Tooltip title="Cancel scraping" placement="top" arrow>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCancelActivity(activity.id);
                              }}
                              disabled={isCancellingActivity(activity.id)}
                              sx={{ ml: 1 }}
                            >
                              {isCancellingActivity(activity.id) ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CancelOutlinedIcon fontSize="small" color="error" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </ListItemButton>
                    );
                  })
                )
              )}
            </Scrollbar>

            {hasNextPage && (
              <ListItemButton
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                sx={{
                  borderRadius: 2,
                  mt: 1,
                  bgcolor: "transparent",
                  cursor: isFetchingNextPage ? "not-allowed" : "pointer",
                }}
              >
                {isFetchingNextPage ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    Load more
                  </Typography>
                )}
              </ListItemButton>
            )}
          </List>

          {/* New Report Button */}
          <Fab
            variant="extended"
            color="warning"
            sx={{
              mt: 2,
              fontWeight: "bold",
              width: "100%",
              borderRadius: 8,
              color: "#fff",
            }}
            onClick={addDialog.onTrue}
          >
            <AddIcon sx={{ mr: 1 }} />
            New Report
          </Fab>
        </Box>
      )}

      {addDialog.value && <ReportsDialog onClose={addDialog.onFalse} open={addDialog.value} />}
    </Stack>
  );
}
